import { useMemo } from "react";
import { useInfiniteQuery } from "react-query";

import { fetchOrder } from "~/api";
import { FetchOrderVariables } from "~/common/types/api";

import { OrderDetailsResponseRaw } from "./useOrderDetails.types";
import {
  getOrderItemsFromOrderDetailsResponse,
  transformOrderDetailsResponse,
} from "./useOrderDetails.util";

export const useOrderDetails = (
  { orderId, customerEmail }: FetchOrderVariables,
  onSuccess?: () => void,
  enabled = true
) => {
  const {
    data: response,
    error,
    isLoading,
    isFetching,
    isError,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery<OrderDetailsResponseRaw, Error>(
    [orderId, customerEmail],
    ({ pageParam = 1 }) =>
      fetchOrder({ orderId, customerEmail, currentPage: pageParam }),
    {
      enabled,
      keepPreviousData: true,
      getNextPageParam: (lastPage, allPages = []) => {
        if (lastPage?.order?.[0]?.more) {
          return allPages.length + 1;
        }
      },
      onSuccess,
    }
  );
  const data = useMemo(
    () => transformOrderDetailsResponse(response?.pages?.[0]),
    [response]
  );
  const orderItems = useMemo(
    () => getOrderItemsFromOrderDetailsResponse(response?.pages),
    [response]
  );
  return {
    isError,
    isLoading: isLoading || isFetching,
    errorMessage: error?.message ?? "",
    data,
    orderItems,
    hasMoreOrderItems: hasNextPage,
    loadMoreOrderItems: fetchNextPage,
  };
};
